<template>
  <div class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 animate-modalFade" v-if="!isClosed">
    <div class="w-full max-w-2xl bg-gray-800 text-indigo-200 rounded-lg flex flex-col shadow-lg border border-gray-600 animate-scaleIn overflow-hidden">

      <!-- ── Header ── -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-gray-800">
        <div class="flex items-center gap-2 flex-wrap">
          <span class="text-xs font-semibold text-gray-300 bg-gray-700 px-2 py-0.5 rounded border border-gray-600">
            <i class="fas fa-map-marker-alt text-indigo-400 mr-1"></i>{{ mapName || 'ไม่ระบุแผนที่' }}
          </span>
          <span
            v-if="timeLeft !== null && !eventData.endBattle"
            class="text-xs font-semibold px-2 py-0.5 rounded border"
            :class="timeLeft <= 420000 ? 'text-red-400 border-red-700 bg-red-900-20' : 'text-yellow-400 border-yellow-700 bg-yellow-900-20'"
          >
            <i class="fas fa-clock mr-1"></i>{{ formatTime(timeLeft) }}
          </span>
          <span v-if="victimData && battleTimeLeft > 0" class="text-xs text-yellow-300 border border-yellow-800 bg-yellow-900-20 px-2 py-0.5 rounded">
            เริ่มได้ใน {{ formatTime(battleTimeLeft) }}
          </span>
          <span v-if="victimData && currentRound > 0" class="text-xs font-bold text-indigo-300 bg-gray-700 border border-gray-600 px-2 py-0.5 rounded">
            รอบที่ {{ currentRound }}
          </span>
        </div>
        <button @click="closeModal" class="text-gray-400 hover:text-red-400 transition-colors ml-2">
          <i class="fas fa-minus"></i>
        </button>
      </div>

      <!-- ── Tabs ── -->
      <div class="flex border-b border-gray-700 bg-gray-800">
        <button
          @click="activeTab = 'battle'"
          class="flex-1 py-2 text-xs font-semibold border-b-2 transition-colors"
          :class="activeTab === 'battle' ? 'border-indigo-500 text-indigo-300' : 'border-transparent text-gray-500 hover:text-gray-300'"
        >
          <i class="fas fa-crosshairs mr-1"></i>การต่อสู้
        </button>
        <button
          @click="activeTab = 'history'"
          class="flex-1 py-2 text-xs font-semibold border-b-2 transition-colors"
          :class="activeTab === 'history' ? 'border-indigo-500 text-indigo-300' : 'border-transparent text-gray-500 hover:text-gray-300'"
        >
          <i class="fas fa-scroll mr-1"></i>บันทึกต่อสู้
          <span v-if="logs.length" class="ml-1 bg-indigo-600 text-white text-xs rounded-full px-1.5 py-0.5">{{ logs.length }}</span>
        </button>
      </div>

      <!-- ── BATTLE TAB ── -->
      <div v-if="activeTab === 'battle'" class="flex flex-col gap-3 p-4 overflow-y-auto ambush-tab-scroll scrollbar-thin">

        <!-- Loading -->
        <div v-if="isLoading" class="flex flex-col items-center py-8 gap-3">
          <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-indigo-500"></div>
          <p class="text-sm text-gray-400">กำลังโหลด...</p>
        </div>

        <!-- Waiting for victim -->
        <div v-else-if="!victimData && !hasReward && !eventData.findVictim && !eventData.endBattle" class="flex flex-col items-center py-6 gap-2">
          <i class="fa-solid fa-mask text-4xl text-gray-600 animate-pulse"></i>
          <p class="text-sm text-indigo-300 font-semibold">กำลังดักรอผู้เล่น...</p>
          <p class="text-xs text-gray-500">ผู้เล่นที่กลับบ้านในแผนที่นี้จะถูกดักทันที</p>
        </div>

        <!-- ── Combat Section ── -->
        <template v-if="victimData && !isLoading">

          <!-- Opponent -->
          <div class="bg-gray-900 rounded-lg border border-red-900 p-3">
            <div class="flex items-center gap-3 mb-2">
              <img
                src="https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/character2%2Fanonymouse.png?alt=media&token=25259b2d-03f1-49fb-b70c-054cd88d2a86"
                alt="ศัตรู"
                class="w-14 h-14 rounded-lg object-cover border-2 border-red-800"
                @error="handleImageError"
              />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-bold text-red-400">บุคคลลึกลับ</p>
                <div class="mt-1 relative h-4 bg-gray-700 rounded overflow-hidden">
                  <div class="absolute inset-0 bg-red-700 rounded" style="width:100%"></div>
                  <span class="absolute inset-0 flex items-center justify-center text-xs text-white font-semibold">HP: ??? / ???</span>
                </div>
              </div>
            </div>
            <!-- Enemy stats row -->
            <div class="grid grid-cols-6 gap-1 text-center mt-1">
              <div v-for="s in enemyStatLabels" :key="s.label" class="bg-gray-800 rounded p-1">
                <i :class="s.icon + ' text-red-400 text-xs'"></i>
                <p class="text-xs text-indigo-300 font-bold mt-0.5">???</p>
                <p class="text-xxs text-gray-500">{{ s.label }}</p>
              </div>
            </div>
          </div>

          <!-- Player -->
          <div class="bg-gray-900 rounded-lg border border-blue-900 p-3">
            <div class="flex items-center gap-3 mb-2">
              <img
                :src="characterData?.imageUrl || 'default-character.png'"
                alt="ผู้เล่น"
                class="w-14 h-14 rounded-lg object-cover border-2 border-blue-700"
                @error="handleImageError"
              />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-bold text-blue-400">{{ characterData?.characterName || 'คุณ' }}</p>
                <div class="mt-1 relative h-4 bg-gray-700 rounded overflow-hidden">
                  <div
                    class="absolute inset-0 rounded transition-all duration-300"
                    :class="hpPercent < 30 ? 'bg-red-600' : 'bg-green-600'"
                    :style="{ width: hpPercent + '%' }"
                  ></div>
                  <span class="absolute inset-0 flex items-center justify-center text-xs text-white font-semibold">
                    HP: {{ currentHealth }} / {{ calculatedStats.maxHp }}
                  </span>
                </div>
              </div>
            </div>
            <!-- Player stats row -->
            <div class="grid grid-cols-6 gap-1 text-center mt-1">
              <div class="bg-gray-800 rounded p-1">
                <i class="fas fa-heartbeat text-blue-400 text-xs"></i>
                <p class="text-xs text-indigo-300 font-bold mt-0.5">{{ calculatedStats.maxHp }}</p>
                <p class="text-xxs text-gray-500">HP</p>
              </div>
              <div class="bg-gray-800 rounded p-1">
                <i class="fas fa-fist-raised text-blue-400 text-xs"></i>
                <p class="text-xs text-indigo-300 font-bold mt-0.5">{{ calculatedStats.atk }}</p>
                <p class="text-xxs text-gray-500">ATK</p>
              </div>
              <div class="bg-gray-800 rounded p-1">
                <i class="fas fa-running text-blue-400 text-xs"></i>
                <p class="text-xs text-indigo-300 font-bold mt-0.5">{{ calculatedStats.evasion }}%</p>
                <p class="text-xxs text-gray-500">หลบ</p>
              </div>
              <div class="bg-gray-800 rounded p-1">
                <i class="fas fa-crosshairs text-blue-400 text-xs"></i>
                <p class="text-xs text-indigo-300 font-bold mt-0.5">{{ calculatedStats.critical }}%</p>
                <p class="text-xxs text-gray-500">คริ</p>
              </div>
              <div class="bg-gray-800 rounded p-1">
                <i class="fa-solid fa-shield-virus text-blue-400 text-xs"></i>
                <p class="text-xs text-indigo-300 font-bold mt-0.5">{{ calculatedStats.criticalReduction }}%</p>
                <p class="text-xxs text-gray-500">ลดคริ</p>
              </div>
              <div class="bg-gray-800 rounded p-1">
                <i class="fas fa-shield-alt text-blue-400 text-xs"></i>
                <p class="text-xs text-indigo-300 font-bold mt-0.5">{{ calculatedStats.defense }}</p>
                <p class="text-xxs text-gray-500">DEF</p>
              </div>
            </div>
          </div>

          <!-- Action buttons -->
          <div class="grid grid-cols-2 gap-2">
            <button
              @click="selectAction('attack')"
              :disabled="isFighting"
              class="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold border transition-all"
              :class="selectedAction === 'attack'
                ? 'bg-red-700 border-red-500 text-white'
                : 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-red-900 hover:bg-opacity-40 hover:border-red-700'"
            >
              <i class="fas fa-fist-raised"></i>
              <span>โจมตี</span>
            </button>
            <button
              @click="selectAction('flee')"
              :disabled="isFighting"
              class="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold border transition-all"
              :class="selectedAction === 'flee'
                ? 'bg-yellow-700 border-yellow-500 text-white'
                : 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-yellow-900 hover:bg-opacity-40 hover:border-yellow-700'"
            >
              <i class="fas fa-running"></i>
              <span>หนี</span>
              <span class="text-xxs font-normal opacity-70">(โอกาสขั้นต่ำ 10%)</span>
            </button>
          </div>

          <!-- Resolve button -->
          <button
            @click="resolveAmbush"
            :disabled="isFighting || resolveTimeLeft > 0 || battleTimeLeft > 0"
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <div v-if="isFighting" class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            <i v-else class="fas fa-bolt"></i>
            <span>ประมวลผลการต่อสู้</span>
            <span v-if="resolveTimeLeft > 0 || battleTimeLeft > 0" class="text-xs opacity-80">
              ({{ formatTime(resolveTimeLeft > 0 ? resolveTimeLeft : battleTimeLeft) }})
            </span>
          </button>
        </template>

        <!-- ── Rewards Section ── -->
        <template v-if="!victimData && hasReward && (eventData.diamondReward > 0 || eventData.goldReward > 0 || eventData.silverReward > 0 || (eventData.rewardItemIds && eventData.rewardItemIds.length > 0)) && !isLoading">
          <!-- Weight bar -->
          <div class="flex items-center justify-between bg-gray-700 rounded-lg px-3 py-2 border border-gray-600">
            <span class="text-xs text-gray-300 flex items-center gap-1">
              <i class="fas fa-weight-hanging text-gray-400"></i>น้ำหนักที่เลือก
            </span>
            <div class="flex items-center gap-2">
              <span class="text-xs font-bold text-indigo-300">{{ selectedWeight }} / {{ capacity }}</span>
              <button
                @click="toggleAllSelections"
                :disabled="isFighting || (!rewards.length && !selectedRewardIds.length)"
                class="text-xs px-2 py-0.5 rounded border font-semibold transition-colors"
                :class="selectedRewardIds.length
                  ? 'bg-yellow-800 border-yellow-600 text-yellow-300 hover:bg-yellow-700'
                  : 'bg-green-800 border-green-600 text-green-300 hover:bg-green-700'"
              >
                <i :class="selectedRewardIds.length ? 'fas fa-rotate-left' : 'fas fa-check-double'" class="mr-1"></i>
                {{ selectedRewardIds.length ? 'ยกเลิก' : 'เลือกทั้งหมด' }}
              </button>
            </div>
          </div>

          <!-- Trash warning -->
          <div class="flex items-center gap-2 text-xs text-yellow-400 bg-yellow-900-20 border border-yellow-800-40 rounded-lg px-3 py-2">
            <i class="fas fa-exclamation-triangle flex-shrink-0"></i>
            <span>รางวัลที่เลือกจะถูกย้ายเข้า `bags.items` ของคุณเมื่อกดใส่กระเป๋า</span>
          </div>

          <!-- Reward list -->
          <ul class="bg-gray-900 rounded-lg border border-gray-700 divide-y divide-gray-700 max-h-52 overflow-y-auto scrollbar-thin">
            <li
              v-for="reward in rewards"
              :key="reward.id"
              @click="toggleRewardSelection(reward.id)"
              class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-800 transition-colors"
              :class="selectedRewardIds.includes(reward.id) ? 'reward-selected border-l-2 border-indigo-500' : ''"
            >
              <img
                :src="reward.img || defaultItemImg"
                class="w-9 h-9 rounded object-cover border border-gray-600 flex-shrink-0"
                @error="handleImageError"
              />
              <div class="flex-1 min-w-0">
                <p class="text-xs font-semibold truncate" :style="reward.rarityColors?.textColor ? { color: reward.rarityColors.textColor } : {}">
                  {{ reward.name }}<span v-if="reward.amount" class="text-gray-400 ml-1">x{{ reward.amount }}</span>
                </p>
                <p class="text-xxs text-gray-500">น้ำหนัก: {{ reward.weight || 0 }}</p>
              </div>
              <i class="flex-shrink-0 text-sm" :class="selectedRewardIds.includes(reward.id) ? 'fas fa-check-circle text-indigo-400' : 'far fa-circle text-gray-600'"></i>
            </li>
            <li v-if="!rewards.length" class="text-center text-gray-500 text-xs py-4">ไม่มีรางวัล</li>
          </ul>

          <!-- Add to bag -->
          <button
            @click="addToBag"
            :disabled="isFighting || !selectedRewardIds.length"
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-700 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <div v-if="isFighting" class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            <i v-else class="fas fa-shopping-bag"></i>
            ใส่กระเป๋า ({{ selectedRewardIds.length }} รายการ)
          </button>
        </template>

        <!-- ── Bottom action buttons ── -->
        <template v-if="!victimData && !isLoading">
          <!-- Cancel waiting -->
          <button
            v-if="!eventData.endBattle && !hasReward"
            @click="handleCancelWaiting"
            :disabled="isFighting"
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border transition-all disabled:opacity-50"
            :class="isConfirmingCancel
              ? 'bg-red-700 border-red-500 text-white animate-confirmPulse'
              : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-red-700 hover:text-red-300'"
          >
            <i class="fas fa-times"></i>
            {{ isConfirmingCancel ? 'ยืนยันยกเลิกการดักรอ?' : 'ยกเลิกการดักรอ' }}
          </button>

          <!-- Hunt ambusher button (เหนือกลับบ้าน) -->
          <!-- ซ่อนเมื่อ: มีรางวัล / การต่อสู้สิ้นสุดแล้ว / เคยมีเหยื่อมาแล้ว (findVictim = true หมายความว่าเคยต่อสู้) -->
          <button
            v-if="!hasReward && !eventData.endBattle && !eventData.findVictim"
            @click="huntAmbusher"
            :disabled="isHunting || isFighting || huntAttempts >= MAX_HUNT_ATTEMPTS"
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            :class="huntAttempts >= MAX_HUNT_ATTEMPTS
              ? 'bg-gray-800 border-gray-700 text-gray-500'
              : 'bg-yellow-900 border-yellow-700 text-yellow-200 hover:bg-yellow-800'"
          >
            <div v-if="isHunting" class="animate-spin rounded-full h-4 w-4 border-t-2 border-yellow-200"></div>
            <i v-else class="fas fa-magnifying-glass text-xs"></i>
            <span v-if="huntAttempts >= MAX_HUNT_ATTEMPTS">ค้นหาครบแล้ว</span>
            <span v-else-if="isHunting">กำลังออกล่า...</span>
            <span v-else>ออกล่าผู้ดักรอ</span>
            <span
              class="text-xxs font-bold ml-1 px-1.5 py-0.5 rounded"
              :class="huntAttempts >= MAX_HUNT_ATTEMPTS ? 'bg-gray-700 text-gray-500' : 'bg-yellow-800 text-yellow-300'"
            >{{ huntAttempts }}/{{ MAX_HUNT_ATTEMPTS }}</span>
          </button>

          <!-- Go home -->
          <button
            v-if="eventData.endBattle"
            @click="handleGoHome"
            :disabled="isFighting"
            class="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border transition-all disabled:opacity-50"
            :class="isConfirmingCancel
              ? 'bg-indigo-700 border-indigo-500 text-white animate-confirmPulse'
              : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-indigo-500 hover:text-indigo-300'"
          >
            <div v-if="isFighting" class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            <i v-else class="fas fa-home"></i>
            {{ isConfirmingCancel ? 'ยืนยันกลับบ้าน?' : 'กลับบ้าน' }}
          </button>
        </template>

        <!-- Result message -->
        <p v-if="fightResult" class="text-xs text-center text-indigo-300 bg-gray-900 bg-opacity-50 rounded-lg px-3 py-2 border border-gray-700">
          {{ fightResult }}
        </p>
      </div>

      <!-- ── HISTORY TAB ── -->
      <div v-if="activeTab === 'history'" class="p-4 overflow-y-auto ambush-tab-scroll scrollbar-thin">
        <div v-if="!filteredLogs.length" class="text-center text-gray-500 py-8">
          <i class="fas fa-scroll text-3xl mb-2 opacity-30 block"></i>
          ยังไม่มีบันทึกการต่อสู้
        </div>
        <ul v-else class="flex flex-col gap-2">
          <li
            v-for="log in filteredLogs"
            :key="log.id"
            class="rounded-lg border text-xs overflow-hidden"
            :class="getLogBorderClass(log)"
          >
            <!-- Log header row -->
            <div class="flex items-center gap-2 px-3 py-1.5 bg-gray-900 bg-opacity-60 border-b border-gray-700">
              <span
                v-if="log.details?.round"
                class="font-bold text-xxs px-1.5 py-0.5 rounded log-round-badge border border-indigo-700 text-indigo-300"
              >รอบ {{ log.details.round }}</span>
              <span class="text-gray-500 flex-1">{{ formatTimestamp(log.timestamp) }}</span>
              <span
                class="font-bold text-xxs px-2 py-0.5 rounded-full"
                :class="getLogBadgeClass(log)"
              >{{ getLogBadgeText(log) }}</span>
            </div>

            <!-- Log body -->
            <div class="px-3 py-2 space-y-2">
              <p class="text-gray-300 leading-relaxed">{{ log.displayMessage }}</p>

              <!-- HP before/after + damage -->
              <div v-if="log.details?.player && !log.details.bothFlee" class="grid grid-cols-2 gap-2">
                <!-- Player side -->
                <div class="bg-gray-900 rounded p-2 border border-blue-900">
                  <p class="text-xxs text-blue-400 font-bold mb-1">คุณ</p>
                  <div class="space-y-0.5">
                    <p class="text-xxs text-gray-400">
                      HP: <span class="text-gray-200 font-semibold">{{ log.details.player.currentHp + log.details.player.damageTaken }}</span>
                      → <span :class="log.details.player.damageTaken > 0 ? 'text-red-400' : 'text-gray-200'" class="font-semibold">{{ log.details.player.currentHp }}</span>
                      / {{ log.details.player.maxHp }}
                    </p>
                    <p v-if="log.details.player.damageDealt > 0 || log.details.player.wasMiss" class="text-xxs">
                      <span class="text-gray-500">โจมตี: </span>
                      <span v-if="log.details.player.wasMiss" class="text-gray-500">พลาด</span>
                      <span v-else :class="log.details.player.wasCrit ? 'text-yellow-400 font-bold' : 'text-orange-300'">
                        {{ log.details.player.damageDealt }}
                        <span v-if="log.details.player.wasCrit" class="text-xxs text-yellow-400"> CRIT!</span>
                      </span>
                    </p>
                    <p v-if="log.details.player.damageTaken > 0" class="text-xxs">
                      <span class="text-gray-500">รับ: </span><span class="text-red-400">-{{ log.details.player.damageTaken }}</span>
                    </p>
                  </div>
                </div>
                <!-- Opponent side -->
                <div class="bg-gray-900 rounded p-2 border border-red-900">
                  <p class="text-xxs text-red-400 font-bold mb-1">บุคคลลึกลับ</p>
                  <div class="space-y-0.5">
                    <p class="text-xxs text-gray-400">
                      HP: <span class="text-gray-200 font-semibold">{{ log.details.opponent.currentHp + log.details.opponent.damageTaken }}</span>
                      → <span :class="log.details.opponent.damageTaken > 0 ? 'text-red-400' : 'text-gray-200'" class="font-semibold">{{ log.details.opponent.currentHp }}</span>
                      / {{ log.details.opponent.maxHp }}
                    </p>
                    <p v-if="log.details.opponent.damageDealt > 0 || log.details.opponent.wasMiss" class="text-xxs">
                      <span class="text-gray-500">โจมตี: </span>
                      <span v-if="log.details.opponent.wasMiss" class="text-gray-500">พลาด</span>
                      <span v-else :class="log.details.opponent.wasCrit ? 'text-yellow-400 font-bold' : 'text-orange-300'">
                        {{ log.details.opponent.damageDealt }}
                        <span v-if="log.details.opponent.wasCrit" class="text-xxs text-yellow-400"> CRIT!</span>
                      </span>
                    </p>
                    <p v-if="log.details.opponent.damageTaken > 0" class="text-xxs">
                      <span class="text-gray-500">รับ: </span><span class="text-red-400">-{{ log.details.opponent.damageTaken }}</span>
                    </p>
                  </div>
                </div>
              </div>

              <!-- Loot summary (only on final log) -->
              <div
                v-if="log.details?.lootDetails && log.details.lootDetails !== 'ไม่มีรางวัล' && log.details.winnerUid"
                class="flex items-start gap-1.5 log-loot-bg border border-yellow-800-40 rounded px-2 py-1.5"
              >
                <i class="fas fa-coins text-yellow-400 text-xs mt-0.5 flex-shrink-0"></i>
                <span class="text-yellow-300 text-xxs leading-relaxed">{{ log.details.lootDetails }}</span>
              </div>
            </div>
          </li>
        </ul>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, onUnmounted, watch } from 'vue';
import { projectFunctions, projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { useInventoryStore } from '@/composables/useInventoryStore';
import gameItems from '@/data/gameItems.json';
import defaultItemImg from '@/assets/default-item.png';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  props: { eventId: { type: String, required: true } },
  emits: ['waitingCompleted', 'close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const inventoryStore = useInventoryStore();
    const playerStore = usePlayerStore();
    const isLoading = ref(true);
    const isClosed = ref(false);
    const activeTab = ref('battle');
    const isFighting = ref(false);
    const fightResult = ref('');
    const isConfirmingCancel = ref(false);

    const victimData = ref(null);
    const characterData = computed(() => playerStore.characterData);
    const characterPrivateData = computed(() => playerStore.characterPrivateData);
    const hasReward = ref(false);
    const rewards = ref([]);
    const selectedRewardIds = ref([]);
    const currentWeight = ref(0);
    const maxLoad = ref(0);
    const mapName = ref('');
    const selectedAction = ref('attack');
    const isAmbusher = ref(false);
    const currentRound = ref(0);
    const logs = ref([]);
    const isHunting = ref(false);
    const huntAttempts = ref(0);
    const MAX_HUNT_ATTEMPTS = 3;
    const currentEventId = ref(props.eventId);

    const eventData = ref({ diamondReward: 0, goldReward: 0, silverReward: 0, findVictim: false, endBattle: false, rewardItemIds: [], rewardItems: {} });

    const timeLeft = ref(null);
    const battleTimeLeft = ref(null);
    const resolveTimeLeft = ref(null);
    let expiresAtMs = null;
    let battleUntilMs = null;
    let nextResolveMs = null;

    let timerMain = null, timerBattle = null, timerResolve = null, confirmTimeout = null;
    let unsubEvent = null, unsubLogs = null;

    const enemyStatLabels = [
      { label: 'HP', icon: 'fas fa-heartbeat' },
      { label: 'ATK', icon: 'fas fa-fist-raised' },
      { label: 'หลบ', icon: 'fas fa-running' },
      { label: 'คริ', icon: 'fas fa-crosshairs' },
      { label: 'ลดคริ', icon: 'fa-solid fa-shield-virus' },
      { label: 'DEF', icon: 'fas fa-shield-alt' },
    ];

    // ── Computed ─────────────────────────────────────────────────────────────
    const calculatedStats = computed(() => {
      if (!characterPrivateData.value) return { maxHp: 0, atk: 0, evasion: 0, critical: 0, criticalReduction: 0, defense: 0 };
      const s = characterPrivateData.value;
      const sum = k =>
        (s.itemStats?.[k] || 0) + (s.bonusStats?.[k] || 0) + (s.stats?.[k] || 0) +
        (s.traitStats?.[k] || 0) + (s.skillStats?.[k] || 0) + (s.titleStats?.[k] || 0) +
        (s.tattooStats?.[k] || 0) + (s.ghostStats?.[k] || 0);
      const str = sum('str'), stamina = sum('stamina'), int = sum('int'), agi = sum('agi'), luck = sum('luck');
      const def = sum('defense');
      const defBonus = Math.floor(def / 20);
      return {
        maxHp: stamina * 20 + sum('maxHp'),
        atk: str * 2 + sum('atk'),
        evasion: Math.floor(agi / 2 + sum('evasion')),
        critical: Math.floor(int / 2 + sum('critical')),
        criticalReduction: luck + defBonus,
        defense: def,
      };
    });

    const currentHealth = computed(() => Math.max(0, calculatedStats.value.maxHp - (characterPrivateData.value?.damageTaken || 0)));
    const hpPercent = computed(() => calculatedStats.value.maxHp > 0 ? Math.max(0, currentHealth.value / calculatedStats.value.maxHp * 100) : 0);
    const selectedWeight = computed(() => rewards.value.filter(r => selectedRewardIds.value.includes(r.id)).reduce((s, r) => s + (r.weight || 0), 0));
    const capacity = computed(() => Math.max(0, (maxLoad.value || 0) - (currentWeight.value || 0)));
    const filteredLogs = computed(() => logs.value);

    // ── Timers ────────────────────────────────────────────────────────────────
    const formatTime = ms => {
      if (!ms || ms <= 0) return '0:00';
      const s = Math.floor(ms / 1000 % 60), m = Math.floor(ms / 60000);
      return `${m}:${s < 10 ? '0' + s : s}`;
    };
    const stopTimer = t => { clearInterval(t); return null; };
    const startTimers = () => {
      timerMain = stopTimer(timerMain);
      timerBattle = stopTimer(timerBattle);
      timerResolve = stopTimer(timerResolve);
      const tick = () => {
        const now = Date.now();
        const BUFFER_MS = 1000; // buffer 1 วิ สำหรับความล่าช้าของเซิร์ฟเวอร์
        timeLeft.value = expiresAtMs ? Math.max(0, expiresAtMs - now) : null;
        battleTimeLeft.value = battleUntilMs ? Math.max(0, battleUntilMs - now + BUFFER_MS) : 0;
        resolveTimeLeft.value = nextResolveMs ? Math.max(0, nextResolveMs - now + BUFFER_MS) : 0;
      };
      tick();
      timerMain = setInterval(tick, 1000);
    };

    // ── Rarity colors ─────────────────────────────────────────────────────────
    const rarityColor = r => {
      const m = { 1: '#9ca3af', 3: '#22c55e', 4: '#3b82f6', 5: '#ef4444', 6: '#a855f7', 7: '#f97316', 9: '#eab308', 10: '#f59e0b' };
      return { textColor: m[r] || '#9ca3af' };
    };

    // ── Fetch rewards ─────────────────────────────────────────────────────────
    const fetchRewards = async (ids) => {
      rewards.value = [];
      if (eventData.value.diamondReward > 0) rewards.value.push({ id: 'diamond', name: 'เพชร', img: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/item2%2F%E0%B8%97%E0%B8%B1%E0%B9%88%E0%B8%A7%E0%B9%84%E0%B8%9B%2F%E0%B9%80%E0%B8%9E%E0%B8%8A%E0%B8%A3.png?alt=media&token=d0f95851-b518-46cd-aa78-4729cc6d9f41', amount: eventData.value.diamondReward, weight: 0, rarityColors: rarityColor(10) });
      if (eventData.value.goldReward > 0) rewards.value.push({ id: 'gold', name: 'ทอง', img: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/item2%2F%E0%B8%97%E0%B8%B1%E0%B9%88%E0%B8%A7%E0%B9%84%E0%B8%9B%2F%E0%B9%80%E0%B8%AB%E0%B8%A3%E0%B8%B5%E0%B8%A2%E0%B8%8D%E0%B8%97%E0%B8%AD%E0%B8%87.png?alt=media&token=e93dc4c5-4a60-4d46-be9e-380bf604df62', amount: eventData.value.goldReward, weight: 0, rarityColors: rarityColor(8) });
      if (eventData.value.silverReward > 0) rewards.value.push({ id: 'silver', name: 'เงิน', img: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/item2%2F%E0%B8%97%E0%B8%B1%E0%B9%88%E0%B8%A7%E0%B9%84%E0%B8%9B%2F%E0%B9%80%E0%B8%AB%E0%B8%A3%E0%B8%B5%E0%B8%A2%E0%B8%8D%E0%B9%80%E0%B8%87%E0%B8%B4%E0%B8%99.png?alt=media&token=2eb615b9-3714-41cb-a1a1-2d268dbb4092', amount: eventData.value.silverReward, weight: 0, rarityColors: rarityColor(6) });
      const rewardItems = eventData.value.rewardItems || {};
      for (const id of (ids || [])) {
        const rewardItem = rewardItems[id];
        if (!rewardItem) continue;
        const template = gameItems[rewardItem.templateId] || {};
        const states = rewardItem.states || {};
        rewards.value.push({
          id,
          instanceId: id,
          templateId: rewardItem.templateId,
          states,
          ...template,
          ...states,
          name: states.name || template.name || rewardItem.templateId,
          type: states.type || template.type || '',
          rarity: states.rarity ?? template.rarity ?? 1,
          rarityColors: states.rarityColors ?? template.rarityColors ?? rarityColor(states.rarity ?? template.rarity ?? 1),
        });
      }
      selectedRewardIds.value = [];
    };

    // ── ค้นหาและต่อสู้กับผู้ดักรออื่น ──────────────────────────────────────
    const huntAmbusher = async () => {
      if (isHunting.value || isFighting.value) return;
      isHunting.value = true;
      fightResult.value = 'กำลังค้นหาผู้ดักรอ...';
      try {
        const res = await callFn({ action: 'huntAmbusher', eventId: currentEventId.value });
        if (res.data.huntAttempts !== undefined) {
          huntAttempts.value = res.data.huntAttempts;
        }
        fightResult.value = res.data.message;
        if (res.data.found && res.data.newEventId) {
          await subscribe(res.data.newEventId);
        }
      } catch (e) {
        fightResult.value = e.message || 'เกิดข้อผิดพลาดในการค้นหา';
      } finally {
        isHunting.value = false;
      }
    };

    // ── Subscribe ─────────────────────────────────────────────────────────────
    const clearSubscriptions = () => {
      if (unsubEvent) { unsubEvent(); unsubEvent = null; }
      if (unsubLogs) { unsubLogs(); unsubLogs = null; }
    };

    const subscribe = async (nextEventId = currentEventId.value) => {
      clearSubscriptions();
      currentEventId.value = nextEventId;
      isLoading.value = true;
      unsubEvent = projectFirestore.collection('events').doc(currentEventId.value).onSnapshot(async doc => {
        if (!doc.exists) { emit('waitingCompleted'); return; }
        const d = doc.data();
        eventData.value = {
          diamondReward: d.diamondReward || 0, goldReward: d.goldReward || 0, silverReward: d.silverReward || 0,
          findVictim: d.findVictim || false, endBattle: d.endBattle || false, rewardItemIds: d.rewardItemIds || [],
          rewardItems: d.rewardItems || {}
        };
        mapName.value = d.map || '';
        hasReward.value = d.hasReward || false;
        isAmbusher.value = d.characterId === user.value?.uid;
        // default คือ 'attack' เสมอ เหมือน backend (ambusherAction || 'attack')
        selectedAction.value = d[isAmbusher.value ? 'ambusherAction' : 'victimAction'] || 'attack';
        currentRound.value = d.round || 0;
        huntAttempts.value = d.huntAttempts || 0;

        // subscribe นับผู้ดักรออื่นในแผนที่เดียวกัน — ถูกลบออกแล้ว ไม่ต้องใช้

        expiresAtMs = d.expiresAt?.toMillis() || null;
        battleUntilMs = d.battleUntil?.toMillis() || null;
        nextResolveMs = d.nextResolve?.toMillis() || null;
        if (!d.endBattle) startTimers();

        if (d.victimUid) {
          victimData.value = { uid: d.victimUid };
        } else {
          victimData.value = null;
          if (hasReward.value) await fetchRewards(d.rewardItemIds);
          else rewards.value = [];
        }
        isLoading.value = false;
      });

      if (user.value) {
        const uid = user.value.uid;
        watch(() => playerStore.characterPrivateData, (newData) => {
          if (!newData?.event) emit('waitingCompleted');
        }, { immediate: true });
        watch(() => inventoryStore.bagData, (bagData) => {
          if (bagData) {
            currentWeight.value = bagData.currentWeight || 0;
            maxLoad.value = bagData.maxLoad || 100;
          }
        }, { immediate: true });

        // FIX #9 – store reference so we can unsubscribe later
        unsubLogs = projectFirestore.collection('logs')
          .where('uid', '==', uid)
          .where('eventId', '==', currentEventId.value)
          .where('functionName', '==', 'resolveAmbush')
          .orderBy('timestamp', 'desc')
          .limit(50)
          .onSnapshot(snap => {
            logs.value = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          });
      }
    };

    // ── Actions ───────────────────────────────────────────────────────────────
    const callFn = async (payload) => {
      const fn = projectFunctions.httpsCallable('gameActions');
      return fn({ ...payload, action: 'ambushActions', subAction: payload.subAction || payload.action });
    };

    const selectAction = async action => {
      if (isFighting.value) return;
      // อนุญาตให้เลือก action เดิมซ้ำได้ (backend จะ throw ถ้าเหมือนกัน — ไม่เป็นไร)
      isFighting.value = true;
      try {
        const res = await callFn({ action: 'selectAction', eventId: currentEventId.value, selectedAction: action });
        fightResult.value = res.data.message;
        selectedAction.value = action;
      } catch (e) {
        // ถ้า backend บอกว่าเลือกซ้ำ ให้ update UI ด้วยค่านั้นเลย
        selectedAction.value = action;
        fightResult.value = e.message || 'เกิดข้อผิดพลาด';
      }
      finally { isFighting.value = false; }
    };

    const resolveAmbush = async () => {
      if (isFighting.value || resolveTimeLeft.value > 0 || battleTimeLeft.value > 0) return;
      isFighting.value = true;
      fightResult.value = 'กำลังประมวลผล...';
      try {
        const res = await callFn({ action: 'resolveAmbush', eventId: currentEventId.value });
        fightResult.value = res.data.message;
      } catch (e) { fightResult.value = e.message || 'เกิดข้อผิดพลาด'; }
      finally { isFighting.value = false; }
    };

    const handleCancelWaiting = () => {
      if (isFighting.value) return;
      if (isConfirmingCancel.value) { cancelWaiting(); isConfirmingCancel.value = false; clearTimeout(confirmTimeout); }
      else {
        isConfirmingCancel.value = true;
        confirmTimeout = setTimeout(() => { isConfirmingCancel.value = false; }, 5000);
      }
    };

    const cancelWaiting = async () => {
      isFighting.value = true;
      try {
        const res = await callFn({ action: 'cancelWaitingLoot', eventId: currentEventId.value });
        fightResult.value = res.data.message;
        emit('waitingCompleted');
      } catch (e) { fightResult.value = e.message || 'เกิดข้อผิดพลาด'; }
      finally { isFighting.value = false; }
    };

    const handleGoHome = () => {
      if (isFighting.value) return;
      if (isConfirmingCancel.value) { goHome(); isConfirmingCancel.value = false; clearTimeout(confirmTimeout); }
      else {
        isConfirmingCancel.value = true;
        confirmTimeout = setTimeout(() => { isConfirmingCancel.value = false; }, 5000);
      }
    };

    const goHome = async () => {
      isFighting.value = true;
      try {
        const res = await callFn({ action: 'goHome', eventId: currentEventId.value });
        fightResult.value = res.data.message;
        emit('waitingCompleted');
      } catch (e) { fightResult.value = e.message || 'เกิดข้อผิดพลาด'; }
      finally { isFighting.value = false; }
    };

    const toggleRewardSelection = id => {
      selectedRewardIds.value = selectedRewardIds.value.includes(id)
        ? selectedRewardIds.value.filter(x => x !== id)
        : [...selectedRewardIds.value, id];
    };

    const toggleAllSelections = () => {
      selectedRewardIds.value = selectedRewardIds.value.length ? [] : rewards.value.map(r => r.id);
    };

    const addToBag = async () => {
      if (isFighting.value || !selectedRewardIds.value.length) return;
      isFighting.value = true;
      try {
        const res = await callFn({ action: 'addToBag', eventId: currentEventId.value, itemIds: selectedRewardIds.value });
        fightResult.value = res.data.message;
        selectedRewardIds.value = [];
        await fetchRewards(eventData.value.rewardItemIds);
      } catch (e) { fightResult.value = e.message || 'เกิดข้อผิดพลาด'; }
      finally { isFighting.value = false; }
    };

    const closeModal = () => { isClosed.value = true; emit('close'); };
    const handleImageError = e => { e.target.src = defaultItemImg; };
    const formatTimestamp = ts => { if (!ts) return ''; const d = ts.toDate ? ts.toDate() : new Date(ts); return d.toLocaleString('th-TH'); };

    // ── Log helpers ───────────────────────────────────────────────────────────
    const getLogBorderClass = log => {
      if (log.details?.winnerUid === user.value?.uid) return 'border-green-800';
      if (log.details?.winnerUid && log.details.winnerUid !== user.value?.uid) return 'border-red-900';
      if (log.details?.bothFlee || log.details?.isFleeSuccessful) return 'border-yellow-900';
      return 'border-gray-700';
    };
    const getLogBadgeClass = log => {
      if (log.details?.winnerUid === user.value?.uid) return 'bg-green-800 text-green-200';
      if (log.details?.winnerUid && log.details.winnerUid !== user.value?.uid) return 'bg-red-900 text-red-300';
      if (log.details?.bothFlee) return 'bg-yellow-900 text-yellow-300';
      if (log.details?.isFleeSuccessful) return 'bg-yellow-900 text-yellow-300';
      return 'bg-gray-700 text-gray-400';
    };
    const getLogBadgeText = log => {
      if (log.details?.winnerUid === user.value?.uid) return 'ชนะ';
      if (log.details?.winnerUid && log.details.winnerUid !== user.value?.uid) return 'แพ้';
      if (log.details?.bothFlee) return 'หนีทั้งคู่';
      if (log.details?.isFleeSuccessful) return 'หนีสำเร็จ';
      if (log.details?.fleeRole !== 'none') return 'หนีไม่สำเร็จ';
      return 'สู้';
    };

    onMounted(subscribe);

    watch(() => props.eventId, (newEventId, oldEventId) => {
      if (newEventId && newEventId !== oldEventId) {
        subscribe(newEventId);
      }
    });

    // FIX #9 – unsubscribe ALL listeners including logs
    onUnmounted(() => {
      stopTimer(timerMain); stopTimer(timerBattle); stopTimer(timerResolve);
      clearTimeout(confirmTimeout);
      clearSubscriptions();
    });

    return {
      isClosed, activeTab, isLoading, isFighting, fightResult, isConfirmingCancel,
      victimData, characterData, characterPrivateData, calculatedStats, currentHealth, hpPercent,
      hasReward, rewards, selectedRewardIds, selectedWeight, capacity, mapName,
      timeLeft, battleTimeLeft, resolveTimeLeft, eventData, selectedAction, isAmbusher,
      currentRound, logs, filteredLogs, enemyStatLabels,
      isHunting, huntAttempts, MAX_HUNT_ATTEMPTS,
      defaultItemImg,
      formatTime, handleImageError, formatTimestamp,
      selectAction, resolveAmbush, handleCancelWaiting, handleGoHome, huntAmbusher,
      toggleRewardSelection, toggleAllSelections, addToBag, closeModal,
      getLogBorderClass, getLogBadgeClass, getLogBadgeText,
    };
  },
};
</script>

<style scoped>
/* ── Scrollbar ── */
.scrollbar-thin::-webkit-scrollbar { width: 4px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #1f2937; border-radius: 9999px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #374151; border-radius: 9999px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #4b5563; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #374151 #1f2937; }

/* ── Custom utilities (replaces TW2-incompatible /opacity syntax) ── */
.text-xxs { font-size: 10px; line-height: 1.2; }

/* max-h arbitrary → custom class */
.ambush-tab-scroll { max-height: 75vh; }

/* bg opacity replacements */
.bg-red-900-20    { background-color: rgba(127, 29, 29, 0.20); }
.bg-yellow-900-20 { background-color: rgba(120, 53, 15, 0.20); }
.bg-orange-900-20 { background-color: rgba(120, 53, 15, 0.18); }
.border-yellow-800-40 { border-color: rgba(92, 45, 11, 0.40); }

/* reward selected highlight */
.reward-selected { background-color: rgba(49, 46, 129, 0.20); }

/* log round badge bg */
.log-round-badge { background-color: rgba(49, 46, 129, 0.60); }

/* log loot bg */
.log-loot-bg { background-color: rgba(120, 53, 15, 0.20); }

/* ── Animations ── */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }

@keyframes scaleIn { from { transform: scale(0.96); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }

@keyframes confirmPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.65; } }
.animate-confirmPulse { animation: confirmPulse 0.6s infinite; }

button:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
